import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { TypedFormBuilder, TypedFormGroup } from 'formsafe';
import { Observable, Subject } from 'rxjs';
import { filter, map, pairwise, takeUntil } from 'rxjs/operators';
import {
  dutyTimeExceeds14Hours,
  endTimeMustBeGreaterThanNextStartTime,
  endTimeMustBeGreaterThanStartTime,
  endTimeMustNotExeed30MinutesFromNow,
  flightLogManaged,
  justificationRequiredForDutyTimesExceeding14Hours,
  justificationRequiredForRestTimesLessThan10Hours,
  restTimeLessThan10Hours,
  startTimeMustBeGreaterThanPreviousEndTime,
  startTimeMustNotExeed30MinutesFromNow,
} from 'src/app/duty-logs/duty-log-form-validators';
import { DutyLogEntity, UpdateDutyLogParams } from 'src/store/duty-log/duty-log.model';
import { getValueChanges } from 'src/util/angular/ng-forms-helpers';
import { field, getValidationErrorsChangesDeep } from 'src/util/angular/ng-forms-validation';
import { kendoDateTime24HourFormat, toDate } from 'src/util/dateLib';

@Component({
  selector: 'fl-duty-edit-form',
  templateUrl: './duty-edit-form.component.html',
  styleUrls: ['./duty-edit-form.component.scss'],
})
export class DutyEditFormComponent implements OnInit, OnDestroy {
  @Input()
  public dutyLogEntry: DutyLogEntity;
  @Input()
  public prevLogEntry: DutyLogEntity;
  @Input()
  public nextLogEntry: DutyLogEntity;

  public form: TypedFormGroup<UpdateDutyLogParams>;
  public dateTimeFormat = kendoDateTime24HourFormat;
  public errors$: Observable<string[]>;
  public dutyTimeExceeds14Hours$: Observable<boolean>;
  public restTimeLessThan10Hours$: Observable<boolean>;

  public isAviationSupervisor$: Observable<boolean>;

  private _onDestroy$ = new Subject();

  constructor(private _fb: TypedFormBuilder) {}

  public ngOnInit() {
    const prevEndTime =
      // tslint:disable-next-line: no-non-null-assertion
      this.prevLogEntry != null ? toDate(this.prevLogEntry.dutyPeriodEndTime!) : null;
    const nextStartTime =
      // tslint:disable-next-line: no-non-null-assertion
      this.nextLogEntry != null ? toDate(this.nextLogEntry.dutyPeriodStartTime!) : null;
    this.form = this._fb.group<UpdateDutyLogParams>(
      {
        id: [this.dutyLogEntry.id, flightLogManaged],
        baseId: [this.dutyLogEntry.baseId, field('Base', { required: true })],
        aircraftId: [this.dutyLogEntry.aircraftId, field('Aircraft', { required: true })],
        employeeId: this.dutyLogEntry.employeeId,
        dutyPeriodStartTime: [
          this.dutyLogEntry.dutyPeriodStartTime,
          field('Duty on time', {
            required: true,
            validators: [
              startTimeMustBeGreaterThanPreviousEndTime(prevEndTime),
              startTimeMustNotExeed30MinutesFromNow,
            ],
          }),
        ],
        dutyPeriodEndTime: [
          this.dutyLogEntry.dutyPeriodEndTime,
          field('Duty off time', { required: !this.dutyLogEntry.isActive }),
        ],
        updatedAt: new Date(),
        isActive: this.dutyLogEntry.isActive,
        comments: [this.dutyLogEntry.comments],
        justification: [this.dutyLogEntry.justification],
        restJustification: [this.dutyLogEntry.restJustification],
      },
      {
        // tslint:disable-next-line: no-non-null-assertion
        validator: Validators.compose([
          justificationRequiredForDutyTimesExceeding14Hours,
          endTimeMustBeGreaterThanStartTime,
          endTimeMustNotExeed30MinutesFromNow,
          endTimeMustBeGreaterThanNextStartTime(nextStartTime),
          justificationRequiredForRestTimesLessThan10Hours(prevEndTime),
        ])!,
      },
    );
    this.errors$ = getValidationErrorsChangesDeep(this.form);
    this.dutyTimeExceeds14Hours$ = getValueChanges(this.form).pipe(map(dutyTimeExceeds14Hours));
    this.restTimeLessThan10Hours$ = getValueChanges(this.form).pipe(
      map((form) => restTimeLessThan10Hours(form, prevEndTime)),
    );

    this.dutyTimeExceeds14Hours$
      .pipe(
        pairwise(),
        filter(([prev, cur]) => prev === true && cur === false),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => this.form.controls.justification.setValue(null));

    this.restTimeLessThan10Hours$
      .pipe(
        pairwise(),
        filter(([prev, cur]) => prev === true && cur === false),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => this.form.controls.restJustification.setValue(null));
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
